import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircle, Mail, Loader2, Crown } from "lucide-react";
import { api, errMsg, tl, waLink } from "@/lib/api";
import { PageTitle } from "@/pages/admin/AdminLayout";

const TEMPLATES = {
  cart_reminder: { label: "Sepette kaldı hatırlatma", wa: (c) => `Merhaba ${c.name}, sepetinizde ${c.cart_items.join(", ") || "ürünler"} bekliyor. Stoklar tükenmeden siparişinizi tamamlayabilirsiniz. onlinesatiyorum.com` },
  viewed_discount: { label: "İncelediniz, indirim yaptık", wa: (c) => `Merhaba ${c.name}, incelediğiniz ${c.viewed_not_bought.slice(0, 3).join(", ") || "ürünlerde"} size özel indirim yaptık! onlinesatiyorum.com` },
  restock: { label: "Maması bitti mi?", wa: (c) => `Merhaba ${c.name}, son siparişinizin üzerinden ${c.days_since_last_order || "bir süre"} gün geçti. Dostunuzun maması bitmek üzereyse sabit müşterilerimize özel kampanyalı fiyatlarımız sizi bekliyor. onlinesatiyorum.com` },
  campaign: { label: "Yeni kampanya", wa: (c) => `Merhaba ${c.name}, kedi ve köpek mamalarında yeni kampanyamız başladı! Fırsatları kaçırmayın: onlinesatiyorum.com` },
};

const FILTERS = [
  ["all", "Tümü", () => true],
  ["loyal", "Sabit Müşteriler", (c) => c.orders_count >= 3],
  ["cart", "Sepette Bırakan", (c) => c.cart_items.length > 0],
  ["viewed", "İnceleyip Almayan", (c) => c.viewed_not_bought.length > 0],
  ["restock", "Maması Bitmiş Olabilir", (c) => c.restock_due],
];

const SEG = { "Sabit Müşteri": "bg-[#172554] text-white", "Tekrar Eden": "bg-blue-100 text-[#172554]", "İlk Alışveriş": "bg-green-100 text-green-800", "Henüz Almadı": "bg-slate-100 text-slate-600" };

function Actions({ c }) {
  const [t, setT] = useState(c.cart_items.length ? "cart_reminder" : c.viewed_not_bought.length ? "viewed_discount" : c.restock_due ? "restock" : "campaign");
  const [busy, setBusy] = useState(false);
  const email = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/customers/${c.id}/email`, { template: t, site_url: window.location.origin });
      toast.success(`${c.email} adresine e-posta gönderildi`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-1.5 min-w-[200px]">
      <select data-testid={`customer-template-${c.id}`} value={t} onChange={(e) => setT(e.target.value)} className="h-8 border rounded px-1 text-xs">
        {Object.entries(TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <div className="flex gap-1">
        <a data-testid={`customer-wa-${c.id}`} href={waLink(c.phone, TEMPLATES[t].wa(c))} target="_blank" rel="noreferrer"
          onClick={() => api.post(`/admin/customers/${c.id}/whatsapp-log`, { template: t }).catch(() => {})}
          className="flex-1 h-8 rounded bg-[#25D366] text-white text-xs font-bold flex items-center justify-center gap-1"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</a>
        <button data-testid={`customer-email-${c.id}`} disabled={busy} onClick={email} className="flex-1 h-8 rounded bg-[#172554] text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}E-posta
        </button>
      </div>
    </div>
  );
}

export default function AdminCustomers() {
  const { data = [] } = useQuery({ queryKey: ["admin-customers"], queryFn: () => api.get("/admin/customers").then((r) => r.data) });
  const [f, setF] = useState("all");
  const fn = FILTERS.find((x) => x[0] === f)[2];
  const list = data.filter(fn);
  return (
    <>
      <PageTitle title="Müşteriler & Sepet Takibi" desc="Kimin ne aldığı, sepette ne bıraktığı, hangi ürüne bakıp almadığı. Tek tıkla WhatsApp veya e-posta ile ulaşın." />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {FILTERS.map(([k, l, fx]) => (
          <button key={k} data-testid={`customers-filter-${k}`} onClick={() => setF(k)} className={`px-4 h-9 rounded-full text-sm font-semibold whitespace-nowrap border ${f === k ? "bg-[#172554] text-white border-[#172554]" : "bg-white"}`}>
            {l} ({data.filter(fx).length})
          </button>
        ))}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-slate-50 text-xs text-slate-600"><tr>{["Müşteri", "Segment", "Sipariş / Harcama", "Son sipariş", "Sepetinde", "İnceleyip almadığı", "Mesaj"].map((h) => <th key={h} className="text-left p-3 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-b align-top" data-testid={`customer-row-${c.id}`}>
                <td className="p-3 min-w-[180px]"><div className="font-semibold">{c.name}</div><div className="text-xs text-slate-500">{c.email}</div><div className="text-xs text-slate-500">{c.phone}</div></td>
                <td className="p-3"><span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded whitespace-nowrap ${SEG[c.segment]}`}>{c.segment === "Sabit Müşteri" && <Crown className="h-3 w-3" />}{c.segment}</span></td>
                <td className="p-3 whitespace-nowrap"><b>{c.orders_count}</b> sipariş<div className="text-xs text-slate-500">{tl(c.total_spent)}</div></td>
                <td className="p-3 text-xs whitespace-nowrap">{c.last_order_at ? <>{new Date(c.last_order_at).toLocaleDateString("tr-TR")}<div className={c.restock_due ? "text-amber-700 font-semibold" : "text-slate-500"}>{c.days_since_last_order} gün önce</div></> : "-"}</td>
                <td className="p-3 text-xs max-w-[200px]">{c.cart_items.length ? c.cart_items.join(", ") : <span className="text-slate-400">Boş</span>}</td>
                <td className="p-3 text-xs max-w-[200px]">{c.viewed_not_bought.length ? c.viewed_not_bought.join(", ") : <span className="text-slate-400">-</span>}</td>
                <td className="p-3"><Actions c={c} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <div className="p-10 text-center text-slate-500">Bu filtrede müşteri yok.</div>}
      </div>
      <p className="text-xs text-slate-500 mt-3">E-postalar sabit şablonlarla tek tek gönderilir (müşteri başına 24 saatte en fazla 2). Toplu duyurular için WhatsApp kullanın.</p>
    </>
  );
}
